import { useMutation } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useNavigate } from 'react-router'
import type { IdVariable } from '@/types/graphql'
import { USE_NAVIGATE_CONFIRM_BYPASS_KEY, useOnErrorAuto } from '@/utils/hooks'
import {
  CREATE_FORM_TEMPLATE_MUTATION,
  DELETE_FORM_TEMPLATE_MUTATION,
  PATCH_FORM_TEMPLATE_MUTATION,
} from './mutations'
import type {
  CreateFormTemplateMutation,
  CreateFormTemplateMutationVariables,
  DeleteFormTemplateMutation,
  PatchFormTemplateMutation,
  PatchFormTemplateMutationVariables,
} from './types.graphql'

export function useFormTemplateMutations(material?: boolean) {
  const translations = useTranslate({
    success: {
      create: 'form.toasts.create-form-success',
      patch: 'form.toasts.patch-form-success',
      delete: 'form.toasts.delete-form-success',
    },
  })

  const navigate = useNavigate()

  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const [createFormTemplate, { loading: createLoading }] = useMutation<
    CreateFormTemplateMutation,
    CreateFormTemplateMutationVariables
  >(CREATE_FORM_TEMPLATE_MUTATION, {
    refetchQueries: [
      'AllFormTemplates',
      'AllFormTemplatesShallow',
      'formTemplate',
    ],
    onCompleted() {
      addToast('success', translations.success.create)
      !material &&
        void navigate('/formcreator', {
          state: { [USE_NAVIGATE_CONFIRM_BYPASS_KEY]: true },
        })
    },
    onError: onErrorAuto(),
  })

  const [patchFormTemplate, { loading: patchLoading }] = useMutation<
    PatchFormTemplateMutation,
    PatchFormTemplateMutationVariables
  >(PATCH_FORM_TEMPLATE_MUTATION, {
    refetchQueries: [
      'AllFormTemplates',
      'AllFormTemplatesShallow',
      'formTemplate',
    ],
    onCompleted() {
      addToast('success', translations.success.patch)
      !material &&
        void navigate('/formcreator', {
          state: { [USE_NAVIGATE_CONFIRM_BYPASS_KEY]: true },
        })
    },
    onError: onErrorAuto(),
  })

  const [deleteFormTemplate, { loading: deleteLoading }] = useMutation<
    DeleteFormTemplateMutation,
    IdVariable
  >(DELETE_FORM_TEMPLATE_MUTATION, {
    refetchQueries: [
      'AllFormTemplates',
      'AllFormTemplatesShallow',
      'formTemplate',
    ],
    onCompleted() {
      addToast('success', translations.success.delete)
      !material &&
        void navigate('/formcreator', {
          state: { [USE_NAVIGATE_CONFIRM_BYPASS_KEY]: true },
        })
    },
    onError: onErrorAuto(),
  })

  return {
    create: createFormTemplate,
    patch: patchFormTemplate,
    delete: deleteFormTemplate,
    loading: createLoading || patchLoading || deleteLoading,
  }
}
